import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import { Autocomplete } from 'components/FormFields';
import { PricingActions } from 'state/modules/pricing';
import { truncateKeywordSpaces } from 'utils/utils';

const CreditTypeSelect = ({
  label,
  variant,
  field: { name, value },
  form: { errors, touched, setFieldValue },
  customParams,
  disabled,
  margin,
  id,
  required,
}) => {
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState('');
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    status: 'ACTIVE',
    deleted: false,
    is_account_level: true,
    ...customParams,
  });

  const { adhocs: creditTypes, fetchingAdhocs: loading } = useSelector((state) => state.pricing);

  const fetchAdHocsTypes = useCallback(
    debounce((params) => {
      const newParams = { ...params };
      setParams(newParams);
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }
      dispatch(PricingActions.fetchAdHocTypes(newParams));
    }, 1000),
    []
  );

  useEffect(() => {
    if (value || value === '') {
      const defaultVal = creditTypes.find((el) => el.id === value);
      if (defaultVal) {
        setDefaultValue(defaultVal);
      } else if (value === '') {
        setDefaultValue('');
      }
    }
  }, [value, creditTypes]);

  const handleFetchAdHocsTypes = () => {
    if (creditTypes.length === 0 && !loading && !params?.keyword)
      dispatch(PricingActions.fetchAdHocTypes(params));
  };

  return (
    <Autocomplete
      label={label}
      margin={margin}
      fullwidth="true"
      variant={variant}
      disabled={disabled}
      value={defaultValue}
      id={id || 'credit-type-select'}
      loading={loading}
      options={creditTypes}
      onHighlightChange={handleFetchAdHocsTypes}
      getOptionLabel={(option) => option.name || ''}
      onChange={(ev, val) => setFieldValue(name, val?.id || '')}
      error={errors && errors[name] && touched && touched[name]}
      getOptionSelected={(option, value) => option.id === value.id}
      onInputChange={(ev, val) => fetchAdHocsTypes({ ...params, keyword: val })}
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
      data-cy="credit-type-selector"
      required={required}
    />
  );
};

CreditTypeSelect.propTypes = {
  label: PropTypes.string,
  margin: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  customParams: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

CreditTypeSelect.defaultProps = {
  required: true,
  disabled: false,
  margin: 'normal',
  form: {
    errors: {},
    touched: {},
  },
  customParams: {},
  variant: 'standard',
  label: 'Select...',
};

export default CreditTypeSelect;
